import {
  Component,
  Inject,
  OnInit,
  ChangeDetectionStrategy
} from '@angular/core';
import { AppService } from '../../app.service';
import { Location } from '@angular/common';
import { UtilsService } from '../../shared/utils.service';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { RBACService } from '../../shared/rbac.service';

@Component({
  selector: 'app-master-charts-popup-dialog',
  templateUrl: './master-charts-popup-dialog.component.html',
  styleUrls: ['./master-charts-popup-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class MasterChartsPopupDialogComponent implements OnInit {
  // The selected tab index which is 0 by default.
  selectedTabIndex: number = 0;

  // RBAC permission flags per tab, resolved once in ngOnInit().
  // A tab is only shown when the user can view the corresponding feature.
  canViewInductions: boolean = false;
  canViewForms: boolean = false;
  canViewHazards: boolean = false;
  canViewIncidents: boolean = false;
  canViewInspectionsAudits: boolean = false;
  canViewMessages: boolean = false;
  canViewSafetyObservations: boolean = false;
  canViewAccessActivities: boolean = false;
  canViewSites: boolean = false;
  canViewTasks: boolean = false;
  canViewToolboxTalks: boolean = false;

  // Visibility of each tab in the same order as listedTabs and the HTML.
  // Used to map the requested tab onto the index of the visible tabs.
  tabVisibility: boolean[] = [];

  // True when the user can view at least one chart tab.
  hasAnyChartAccess: boolean = false;

  // Keep this list in the same order as its placement in the html page.
  listedTabs: string[] = [
    'Account Inductions',
    'Forms',
    'Hazards & Risks',
    'Incidents',
    'Inspections & Audits',
    'Messages',
    'Safety Observations',
    'Access Activities',
    this.utils.getLangTerm('parent-child-sites-combined.plural', 'Sites'),
    'Tasks',
    this.utils.getLangTerm('toolbox-talks.plural', 'Toolbox Talks')
  ];

  // Set the realtime data polling to false.
  enable_realtime_data: boolean = false;
  // Set the default realtime data polling interval to 60 seconds..
  realtime_data_interval_seconds: number = 60;

  // Set the default query type to single. This can only be single or multiple.
  query_type: string = 'single';

  constructor(
    private app: AppService,
    public location: Location,
    public utils: UtilsService,
    public rbac: RBACService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<MasterChartsPopupDialogComponent>
  ) {}

  ngOnInit() {
    // 1. Resolve permission flags per tab. Each tab is visible only when the account's
    // subscription plan unlocks the feature (planCan) AND the user has the RBAC view
    // permission(s). Both mirror the plan + RBAC gates enforced by the chart endpoints in the API.
    // Tasks is a free feature, so planCan(TASKS) is always true.
    this.canViewInductions =
      this.app.planCan(AppService.FEATURES.INDUCTIONS) &&
      this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_ACCOUNT_INDUCTIONS,
            RBACService.RBAC_A.A_VIEW
          )
        ],
        true
      );
    this.canViewForms =
      this.app.planCan(AppService.FEATURES.FORMS) &&
      this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_FORMS,
            RBACService.RBAC_A.A_VIEW
          )
        ],
        true
      );
    this.canViewHazards =
      this.app.planCan(AppService.FEATURES.HAZARDS) &&
      this.rbac.userCanAccess([
        RBACService.getPermission(
          RBACService.RBAC_F.F_REGISTERS_HAZARDS_RISKS,
          RBACService.RBAC_A.A_VIEW
        ),
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES_HAZARDS_RISKS,
          RBACService.RBAC_A.A_VIEW
        )
      ]);
    this.canViewIncidents =
      this.app.planCan(AppService.FEATURES.INCIDENTS) &&
      this.rbac.userCanAccess([
        RBACService.getPermission(
          RBACService.RBAC_F.F_REGISTERS_INCIDENTS,
          RBACService.RBAC_A.A_VIEW
        ),
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES_INCIDENTS,
          RBACService.RBAC_A.A_VIEW
        )
      ]);
    this.canViewInspectionsAudits =
      this.app.planCan(AppService.FEATURES.INSPECTIONS_AUDITS) &&
      this.rbac.userCanAccess([
        RBACService.getPermission(
          RBACService.RBAC_F.F_REGISTERS_INSPECTIONS_AUDITS,
          RBACService.RBAC_A.A_VIEW
        ),
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES_INSPECTIONS_AUDITS,
          RBACService.RBAC_A.A_VIEW
        )
      ]);
    this.canViewMessages =
      this.app.planCan(AppService.FEATURES.MESSAGES) &&
      this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_MESSAGES,
            RBACService.RBAC_A.A_VIEW
          )
        ],
        true
      );
    this.canViewSafetyObservations =
      this.app.planCan(AppService.FEATURES.SAFETY_OBSERVATIONS) &&
      this.rbac.userCanAccess([
        RBACService.getPermission(
          RBACService.RBAC_F.F_REGISTERS_SAFETY_OBSERVATIONS,
          RBACService.RBAC_A.A_VIEW
        ),
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES_SAFETY_OBSERVATIONS,
          RBACService.RBAC_A.A_VIEW
        )
      ]);
    this.canViewAccessActivities =
      this.app.planCan(AppService.FEATURES.SITE_ACCESS) &&
      this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_REPORTS_SITE_ACCESS,
            RBACService.RBAC_A.A_VIEW
          )
        ],
        true
      );
    this.canViewSites =
      this.app.planCan(AppService.FEATURES.SITES) &&
      this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES,
            RBACService.RBAC_A.A_VIEW
          )
        ],
        true
      );
    this.canViewTasks =
      this.app.planCan(AppService.FEATURES.TASKS) &&
      this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_TASKS,
            RBACService.RBAC_A.A_VIEW
          )
        ],
        true
      );
    this.canViewToolboxTalks =
      this.app.planCan(AppService.FEATURES.TOOLBOX_TALKS) &&
      this.rbac.userCanAccess([
        RBACService.getPermission(
          RBACService.RBAC_F.F_REGISTERS_TOOLBOX_TALKS,
          RBACService.RBAC_A.A_VIEW
        ),
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES_TOOLBOX_TALKS,
          RBACService.RBAC_A.A_VIEW
        )
      ]);

    // 2. Build the visibility array in the same order as listedTabs / the HTML.
    this.tabVisibility = [
      this.canViewInductions,
      this.canViewForms,
      this.canViewHazards,
      this.canViewIncidents,
      this.canViewInspectionsAudits,
      this.canViewMessages,
      this.canViewSafetyObservations,
      this.canViewAccessActivities,
      this.canViewSites,
      this.canViewTasks,
      this.canViewToolboxTalks
    ];

    // The user can view at least one chart tab.
    this.hasAnyChartAccess = this.tabVisibility.some(Boolean);

    // 3. Resolve the selected tab against the VISIBLE tabs. Hiding tabs shifts
    // the rendered indices, so the requested tab's index must be recalculated
    // as the number of visible tabs that precede it.
    if (
      typeof this.data['selectedTab'] != 'undefined' &&
      this.data['selectedTab'] != ''
    ) {
      // Get the requested tab index in the full (unfiltered) list.
      const requestedIndex: number = this.listedTabs.indexOf(
        this.data['selectedTab']
      );
      // Only honour it if the requested tab is both valid and visible to the user.
      if (requestedIndex > -1 && this.tabVisibility[requestedIndex]) {
        // Count how many visible tabs precede the requested tab.
        this.selectedTabIndex = this.tabVisibility
          .slice(0, requestedIndex)
          .filter(Boolean).length;
      }
    }
  }
}
